/**
 * SharedStyles
 *
 * Injects global styles that cannot be expressed in index.css because they
 * need to be scoped to components rendered outside the normal CSS cascade
 * (e.g. portals, third-party modals).
 *
 * Font import is in index.css — the single canonical location.
 * All color values reference CSS variables so they respond to theme switches.
 */
import React from 'react';

const SharedStyles = () => {
  return (
    <style>{`
      * {
        font-family: var(--font-sans);
      }

      h1, h2 {
        font-family: var(--font-sans);
        font-weight: 600 !important;
      }

      h3, h4, h5, h6 {
        font-family: var(--font-sans);
        font-weight: 500 !important;
      }

      .text-xl, .text-2xl, .text-3xl, .text-4xl, .text-5xl, .text-6xl {
        font-family: var(--font-sans) !important;
        font-weight: 700 !important;
      }

      .font-bold {
        font-family: var(--font-sans) !important;
        font-weight: 700 !important;
      }

      .font-semibold {
        font-family: var(--font-sans) !important;
        font-weight: 600 !important;
      }

      .font-mono, .mono {
        font-family: var(--font-mono) !important;
        font-weight: 700 !important;
        font-variant-numeric: tabular-nums;
      }

      @keyframes slideUp {
        from { opacity: 0; transform: translateY(30px); }
        to   { opacity: 1; transform: translateY(0);    }
      }

      @keyframes fadeIn {
        from { opacity: 0; }
        to   { opacity: 1; }
      }

      @keyframes slideInUp {
        from { opacity: 0; transform: translateY(20px); }
        to   { opacity: 1; transform: translateY(0);    }
      }

      @keyframes shimmer {
        0%   { transform: translateX(-100%); }
        100% { transform: translateX(100%);  }
      }

      .animate-slide-up  { animation: slideUp   0.6s ease-out; }
      .animate-fade-in   { animation: fadeIn    0.4s ease-out; }
      .animate-slide-in  { animation: slideInUp 0.5s ease-out; }

      /* ── Glassmorphism ── */
      .glassmorphism {
        background:
          linear-gradient(180deg, rgba(99, 102, 241, 0.06) 0%, rgba(99, 102, 241, 0.02) 100%),
          color-mix(in srgb, var(--color-surface) 88%, transparent);
        backdrop-filter: blur(16px);
        border: 1px solid var(--color-border);
        box-shadow:
          inset 0 1px 0 rgba(255, 255, 255, 0.08),
          inset 0 -1px 0 rgba(0, 0, 0, 0.2),
          0 20px 40px rgba(0, 0, 0, 0.6),
          0 1px 2px rgba(0, 0, 0, 0.3);
      }

      [data-theme="light"] .glassmorphism {
        background:
          linear-gradient(180deg, rgba(255, 255, 255, 0.85) 0%, rgba(255, 255, 255, 0.7) 100%),
          color-mix(in srgb, var(--color-surface) 78%, transparent);
        box-shadow:
          inset 0 1px 0 rgba(255, 255, 255, 0.9),
          inset 0 -1px 0 rgba(0, 0, 0, 0.04),
          0 4px 16px rgba(0, 0, 0, 0.08),
          0 1px 2px rgba(0, 0, 0, 0.04);
      }

      /* ── Card glow ── */
      .card-glow {
        box-shadow:
          inset 0 1px 0 rgba(255, 255, 255, 0.08),
          inset 0 -1px 0 rgba(0, 0, 0, 0.25),
          var(--shadow-card);
      }

      [data-theme="light"] .card-glow {
        box-shadow:
          inset 0 1px 0 rgba(255, 255, 255, 0.9),
          inset 0 -1px 0 rgba(0, 0, 0, 0.04),
          var(--shadow-card);
      }

      .card-glow-hover {
        transition: transform 0.15s ease-out, box-shadow 0.15s ease-out, background 0.15s ease-out;
      }

      .card-glow-hover:hover {
        transform: translateY(-2px);
        box-shadow: var(--shadow-card-hover);
      }

      /* ── Input focus ── */
      input:focus, select:focus, textarea:focus {
        outline: none;
        border-color: var(--color-accent);
        box-shadow: 0 0 0 3px var(--color-focus-ring);
      }

      /* ── Autofill overrides — CSS vars so both themes work ── */
      input:-webkit-autofill,
      input:-webkit-autofill:hover,
      input:-webkit-autofill:focus,
      input:-webkit-autofill:active {
        -webkit-text-fill-color: var(--color-text-primary) !important;
        caret-color: var(--color-text-primary) !important;
        -webkit-box-shadow: 0 0 0 1000px var(--color-surface-input) inset !important;
        box-shadow: 0 0 0 1000px var(--color-surface-input) inset !important;
        transition: background-color 99999s ease-in-out 0s !important;
      }

      input:-moz-autofill,
      input:-moz-autofill:hover,
      input:-moz-autofill:focus {
        color: var(--color-text-primary) !important;
      }

      /* ── Progress bar shimmer ── */
      .progress-bar {
        position: relative;
        overflow: hidden;
      }

      .progress-bar::after {
        content: '';
        position: absolute;
        top: 0; left: 0; right: 0; bottom: 0;
        background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.15), transparent);
        animation: shimmer 2s infinite;
      }

      [data-theme="light"] .progress-bar::after {
        background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.5), transparent);
      }

      /* ── Gradient text ── */
      .gradient-text {
        background: linear-gradient(135deg, var(--color-accent) 0%, var(--color-accent-strong) 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
      }
    `}</style>
  );
};

export default SharedStyles;
